import axios from "axios";

import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  TwitterAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  getAuth,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  setDoc,
  serverTimestamp,
  deleteDoc,
  getDoc,
  updateDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyA2RQqX0ZsAafzgqAxfNQQ-lrCFSlmmyUA",
  authDomain: "kaizenflow-3790d.firebaseapp.com",
  projectId: "kaizenflow-3790d",
  storageBucket: "kaizenflow-3790d.appspot.com",
  messagingSenderId: "1037131793386",
  appId: "1:1037131793386:web:b9b814ccd601d655152893",
  measurementId: "G-73WRE5HB7S",
};

const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
const db = getFirestore(app);

const googleProvider = new GoogleAuthProvider();
const twitterProvider = new TwitterAuthProvider();

const signInWithGoogle = async () => {
  try {
    const res = await signInWithPopup(auth, googleProvider);
    const user = res.user;
    axios.post("/api/createUser", { user });
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

const signInWithTwitter = async () => {
  try {
    const res = await signInWithPopup(auth, twitterProvider);
    const user = res.user;
    const { username } = axios.post("/api/createUser", { user });
    return username;
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

const logInWithEmailAndPassword = async (email, password) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

const registerWithEmailAndPassword = async (name, email, password) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    await addDoc(collection(db, "users"), {
      uid: user.uid,
      name,
      authProvider: "local",
      email,
    });
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    alert("Password reset link sent!");
  } catch (err) {
    console.error(err);
    alert(err.message);
  }
};

const logout = () => {
  signOut(auth);
};

const roomRef = (roomId) => doc(db, "rooms", roomId);
const participantsRef = (roomId, participantId) =>
  doc(db, "rooms", roomId, "participants", participantId);
const todosRef = (roomId, participantId) =>
  doc(db, "rooms", roomId, "todos", participantId);
const signalsCollection = (roomId) =>
  collection(db, "rooms", roomId, "signals");
// Session data is stored in the room document itself under a 'session' field
const sessionRef = (roomId) => roomRef(roomId);

const createRoomDocument = async ({ roomId, creatorName, creatorId }) => {
  const expiresInMs = 1000 * 60 * 60 * 6; // 6 hours
  await setDoc(
    roomRef(roomId),
    {
      createdAt: serverTimestamp(),
      active: true,
      creatorName: creatorName || "guest",
      ...(creatorId ? { creatorId } : {}),
      expiresAt: Date.now() + expiresInMs,
    },
    { merge: true }
  );
  return roomRef(roomId);
};

const ensureParticipant = async ({ roomId, participantId, displayName }) => {
  const ref = participantsRef(roomId, participantId);
  await setDoc(
    ref,
    {
      displayName: displayName || "Guest",
      joinedAt: serverTimestamp(),
      lastSeen: serverTimestamp(),
    },
    { merge: true }
  );
  return ref;
};

const updateParticipantHeartbeat = async ({ roomId, participantId }) => {
  const ref = participantsRef(roomId, participantId);
  await updateDoc(ref, { lastSeen: serverTimestamp() });
};

const leaveRoom = async ({ roomId, participantId }) => {
  await deleteDoc(participantsRef(roomId, participantId));
};

const writeTodoSnapshot = async ({ roomId, participantId, todos }) => {
  await setDoc(
    todosRef(roomId, participantId),
    {
      todos,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

const writeSignal = async ({ roomId, payload }) => {
  await addDoc(signalsCollection(roomId), {
    ...payload,
    createdAt: serverTimestamp(),
  });
};

const writeRoomSession = async ({ roomId, duration, mode, status, paused }) => {
  const sessionData = {
    duration,
    mode,
    status,
    paused: paused || false,
    updatedAt: serverTimestamp(),
  };
  
  if (status === "running" && !paused) {
    sessionData.startedAt = serverTimestamp();
  }
  
  await setDoc(
    roomRef(roomId),
    {
      session: sessionData,
    },
    { merge: true }
  );
};

const getRoomSession = async (roomId) => {
  const snapshot = await getDoc(roomRef(roomId));
  if (snapshot.exists()) {
    const data = snapshot.data();
    return { exists: () => !!data.session, data: () => data.session };
  }
  return { exists: () => false, data: () => null };
};

const getRoom = (roomId) => getDoc(roomRef(roomId));

export {
  auth,
  db,
  signInWithGoogle,
  signInWithTwitter,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  sendPasswordReset,
  logout,
  createRoomDocument,
  ensureParticipant,
  updateParticipantHeartbeat,
  leaveRoom,
  writeTodoSnapshot,
  writeSignal,
  writeRoomSession,
  getRoomSession,
  getRoom,
  participantsRef,
  todosRef,
  signalsCollection,
  sessionRef,
};
